import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Ranking, Tournament } from '../../assets/images';
import { TournamentLine } from '../../pages/championships/components/tournamentLine';
import { TournamentLine as TournamentLine2 } from '../../pages/championships/components/tournamentLine2';
import { TournamentLine as TournamentLine3 } from '../../pages/championships/components/tournamentLine3';
import { TournamentLineFinal } from '../../pages/championships/components/tournamentLineFinal';
import { TournamentSlot } from '../../pages/championships/components/tournamentSlot';
import { formatDateTimeDisplay } from '../../utils/base';
import { ChampionBannerLeft, ChampionBannerRight, ChampionBannerTop } from '../../assets/images/championBanner';
import { ContentsIconDefault } from '../../assets/images/contentsIconDefault';
import { rankAPI, logAPI, userAPI } from '../../actions/imsServer';
import { API_PAGE_SIZE } from '../../constants';

export const ChampionshipDetailContent = ({ championshipData }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    // 현재 사용자 정보
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 랭킹 데이터 상태
    const [rankingData, setRankingData] = useState([]);
    // 토너먼트 브래킷 데이터 상태
    const [tournamentBracket, setTournamentBracket] = useState({
        round16: [], // 16강 데이터 (16개)
        round8: [],  // 8강 데이터 (8개)
        round4: [], // 4강 데이터 (4개)
        final: []   // 결승 데이터 (2개)
    });
    const [loading, setLoading] = useState(true);

    // 토너먼트 브래킷 데이터 매핑 함수
    const mapTournamentBracket = (ranks, playerCount = 16, gameMatches = []) => {
        const result = {
            round16: [],
            round8: [],
            round4: [],
            final: []
        };

        // 게임 매칭 정보를 userId로 빠르게 찾기 위한 맵 생성
        const matchMap = new Map();
        gameMatches.forEach((match, matchIndex) => {
            if (match.players.length >= 2) {
                const player1 = match.players[0];
                const player2 = match.players[1];
                // 각 매치의 인덱스를 playerId 조합으로 저장
                const key1 = `${player1.userId}_${player2.userId}`;
                const key2 = `${player2.userId}_${player1.userId}`;
                matchMap.set(key1, { matchIndex, match });
                matchMap.set(key2, { matchIndex, match });
            }
        });

        // 시작 라운드 결정
        let currentRound = [];
        let roundName = '';

        if (playerCount === 16) {
            // 16강부터 시작
            currentRound = ranks.slice(0, 16).map((item, index) => ({
                ...item,
                bracketPosition: index,
                round: 'round16'
            }));
            roundName = 'round16';
            result.round16 = currentRound;
        } else if (playerCount === 8) {
            // 8강부터 시작
            // 실제 게임 매칭 결과를 사용하여 8강 슬롯 구성
            // gameMatches에서 8강 게임(가장 하위 라운드 게임)을 찾아서 배치
            const round8Slots = new Array(8).fill(null);
            let slotIndex = 0;

            // gameMatches에서 실제 게임이 이루어진 매치들을 찾아 8강 슬롯에 배치
            // 각 게임의 플레이어들을 같은 매치에 배치
            // 단, 3번째 팀(round8[4], round8[5])과 4번째 팀(round8[6], round8[7])은 경기 전이므로 배치하지 않음
            gameMatches.forEach((match, matchIndex) => {
                if (match && match.players && match.players.length >= 2) {
                    // 이 게임이 8강 게임인지 확인 (모든 플레이어가 8강 플레이어이어야 함)
                    // ranks에 포함된 플레이어들이면 8강 플레이어
                    const rankUserIds = new Set(ranks.slice(0, 8).map(r => r.userId));
                    const matchPlayerIds = match.players.map(p => p.userId);
                    const allPlayersInRound8 = matchPlayerIds.every(id => rankUserIds.has(id));

                    // 8강일 때는 첫 번째와 두 번째 팀만 채움 (slotIndex < 4)
                    if (allPlayersInRound8 && slotIndex < 4) {
                        // 8강 게임: 두 플레이어를 같은 매치에 배치
                        const player1Data = ranks.find(r => r.userId === matchPlayerIds[0]);
                        const player2Data = ranks.find(r => r.userId === matchPlayerIds[1]);

                        // 같은 매치의 matchIndex 계산 (slotIndex는 짝수여야 함)
                        const currentMatchIndex = Math.floor(slotIndex / 2);

                        if (player1Data && slotIndex < 4) {
                            round8Slots[slotIndex] = {
                                ...player1Data,
                                bracketPosition: slotIndex,
                                round: 'round8',
                                matchIndex: currentMatchIndex,
                                position: 0
                            };
                            slotIndex++;
                        }

                        if (player2Data && slotIndex < 4) {
                            round8Slots[slotIndex] = {
                                ...player2Data,
                                bracketPosition: slotIndex,
                                round: 'round8',
                                matchIndex: currentMatchIndex,
                                position: 1
                            };
                            slotIndex++;
                        }
                    }
                }
            });

            // 게임에 참여하지 않은 플레이어들을 나머지 슬롯에 배치
            // 단, 3번째 팀(round8[4], round8[5])과 4번째 팀(round8[6], round8[7])은 경기 전이므로 배치하지 않음
            const usedUserIds = new Set(round8Slots.filter(s => s).map(s => s.userId));
            ranks.slice(0, 8).forEach((item, index) => {
                if (!usedUserIds.has(item.userId)) {
                    // 빈 슬롯 찾기 (단, 3번째 팀과 4번째 팀 슬롯은 제외)
                    for (let i = 0; i < 4; i++) { // 첫 번째와 두 번째 팀만 채움 (round8[0]~round8[3])
                        if (!round8Slots[i]) {
                            round8Slots[i] = {
                                ...item,
                                bracketPosition: i,
                                round: 'round8',
                                matchIndex: Math.floor(i / 2),
                                position: i % 2
                            };
                            usedUserIds.add(item.userId);
                            break;
                        }
                    }
                }
            });

            // round8Slots 배열을 그대로 사용 (인덱스 보존)
            // 3번째 팀(round8[4], round8[5])과 4번째 팀(round8[6], round8[7])은 null로 유지됨
            // 하지만 result.round8에는 유효한 값만 저장 (null 제외)
            currentRound = round8Slots.filter(s => s);
            roundName = 'round8';
            // result.round8에는 전체 배열 저장 (null 포함) - 렌더링에서 인덱스 접근을 위해
            result.round8 = round8Slots;
        } else if (playerCount === 4) {
            // 4강부터 시작
            currentRound = ranks.slice(0, 4).map((item, index) => ({
                ...item,
                bracketPosition: index,
                round: 'round4'
            }));
            roundName = 'round4';
            result.round4 = currentRound;
        } else if (playerCount === 2) {
            // 결승부터 시작
            currentRound = ranks.slice(0, 2).map((item, index) => ({
                ...item,
                bracketPosition: index,
                round: 'final'
            }));
            roundName = 'final';
            result.final = currentRound;
            return result;
        }

        // 다음 라운드로 진행
        // prevRound는 하위 라운드의 플레이어들 (2명씩 같은 매치)
        // 상위 라운드에서는 하위 라운드 매치의 승자들이 새로운 매치를 구성
        const processRound = (prevRound, targetRoundName, targetResultKey, startMatchIndex = 0) => {
            const nextRound = [];

            // 먼저 하위 라운드의 각 매치에서 승자를 찾아서 승자 배열 생성
            const winners = [];
            for (let i = 0; i < prevRound.length; i += 2) {
                const player1 = prevRound[i];
                const player2 = prevRound[i + 1];

                if (player1 && player2) {
                    // 하위 라운드 매치의 결과 찾기
                    let foundMatch = null;
                    for (const match of gameMatches) {
                        if (match && match.players && match.players.length >= 2) {
                            const hasPlayer1 = match.players.some(p => p.userId === player1.userId);
                            const hasPlayer2 = match.players.some(p => p.userId === player2.userId);
                            if (hasPlayer1 && hasPlayer2 && match.winner) {
                                foundMatch = match;
                                break;
                            }
                        }
                    }

                    // 승자 결정
                    if (foundMatch && foundMatch.winner) {
                        if (foundMatch.winner === player1.userId) {
                            winners.push(player1);
                        } else if (foundMatch.winner === player2.userId) {
                            winners.push(player2);
                        } else {
                            // 매칭 실패시 wincount로 결정
                            if (player1.wincount >= player2.wincount) {
                                winners.push(player1);
                            } else {
                                winners.push(player2);
                            }
                        }
                    } else {
                        // 게임이 진행되지 않았을 때의 처리
                        // 4강 또는 결승으로 진행할 때는 게임이 없으면 승자를 추가하지 않음
                        // 8강으로 진행할 때만 첫 번째 플레이어를 승자로 추가 (대기 상태로 표시하기 위해)
                        if (targetRoundName === 'round8') {
                            // 8강으로 진행: 첫 번째 플레이어를 승자로 추가 (대기 상태로 표시)
                            winners.push(player1);
                        }
                        // 4강 또는 결승으로 진행할 때는 게임이 없으면 승자를 추가하지 않음
                    }
                } else if (player1) {
                    winners.push(player1);
                } else if (player2) {
                    winners.push(player2);
                }
            }

            // 승자들을 2명씩 묶어서 상위 라운드 매치 구성
            for (let i = 0; i < winners.length; i += 2) {
                const player1 = winners[i];
                const player2 = winners[i + 1];
                const matchIndex = i / 2;

                if (player1 && player2) {
                    // 상위 라운드 게임이 진행되었는지 확인
                    let foundMatch = null;
                    for (const match of gameMatches) {
                        if (match && match.players && match.players.length >= 2) {
                            const hasPlayer1 = match.players.some(p => p.userId === player1.userId);
                            const hasPlayer2 = match.players.some(p => p.userId === player2.userId);
                            if (hasPlayer1 && hasPlayer2) {
                                foundMatch = match;
                                break;
                            }
                        }
                    }

                    if (foundMatch && foundMatch.winner) {
                        // 게임이 진행된 경우: 두 플레이어를 모두 추가
                        if (foundMatch.winner === player1.userId) {
                            // player1이 승자
                            nextRound.push({
                                ...player1,
                                bracketPosition: matchIndex * 2,
                                round: targetRoundName,
                                matchIndex: matchIndex,
                                position: 0,
                                winnerIndex: matchIndex * 2
                            });
                            // player2는 패자
                            nextRound.push({
                                ...player2,
                                bracketPosition: matchIndex * 2 + 1,
                                round: targetRoundName,
                                matchIndex: matchIndex,
                                position: 1
                            });
                        } else {
                            // player2가 승자
                            nextRound.push({
                                ...player1,
                                bracketPosition: matchIndex * 2,
                                round: targetRoundName,
                                matchIndex: matchIndex,
                                position: 0
                            });
                            nextRound.push({
                                ...player2,
                                bracketPosition: matchIndex * 2 + 1,
                                round: targetRoundName,
                                matchIndex: matchIndex,
                                position: 1,
                                winnerIndex: matchIndex * 2 + 1
                            });
                        }
                    } else {
                        // 게임이 진행되지 않은 경우
                        // 8강으로 진행할 때는 두 플레이어를 모두 추가 (대기 상태)
                        // 4강 또는 결승으로 진행할 때는 플레이어를 추가하지 않음 (빈 슬롯)
                        if (targetRoundName === 'round8') {
                            // 8강으로 진행: 두 플레이어를 모두 추가 (대기 상태)
                            nextRound.push({
                                ...player1,
                                bracketPosition: matchIndex * 2,
                                round: targetRoundName,
                                matchIndex: matchIndex,
                                position: 0
                            });
                            nextRound.push({
                                ...player2,
                                bracketPosition: matchIndex * 2 + 1,
                                round: targetRoundName,
                                matchIndex: matchIndex,
                                position: 1
                            });
                        }
                        // 4강 또는 결승으로 진행할 때는 게임이 없으면 플레이어를 추가하지 않음
                    }
                } else if (player1) {
                    nextRound.push({
                        ...player1,
                        bracketPosition: matchIndex * 2,
                        round: targetRoundName,
                        matchIndex: matchIndex,
                        position: 0
                    });
                }
            }

            result[targetResultKey] = nextRound;
            return nextRound;
        };

        // 16강이 있으면 8강 생성
        if (playerCount === 16 && currentRound.length > 0) {
            currentRound = processRound(result.round16, 'round8', 'round8', 0);
        }

        // 8강이 있으면 4강 생성 (16강이거나 8강 시작인 경우)
        // 이제 8강 슬롯이 실제 게임 매칭 결과를 반영하므로, processRound를 사용하여 4강 생성
        // result.round8이 null을 포함할 수 있으므로, 유효한 값이 있는지 확인
        const hasValidRound8 = result.round8 && result.round8.some(s => s !== null);
        if (hasValidRound8 && (playerCount === 16 || playerCount === 8)) {
            // processRound를 사용하되, gameMatches에서 실제 승자를 찾아야 함
            // processRound는 2명씩 묶어서 처리하므로, 각 매치의 승자를 찾아야 함
            const round8ForProcess = [];

            // result.round8을 matchIndex 순서대로 정렬하여 processRound에 전달
            // 8강일 때는 첫 번째와 두 번째 팀만 처리 (matchIndex 0, 1)
            const maxMatchIndex = playerCount === 8 ? 2 : 4;
            for (let matchIndex = 0; matchIndex < maxMatchIndex; matchIndex++) {
                // round8Slots 배열은 인덱스 기반이므로 직접 인덱스로 접근
                const slotIndex1 = matchIndex * 2;
                const slotIndex2 = matchIndex * 2 + 1;
                const player1 = result.round8[slotIndex1] || null;
                const player2 = result.round8[slotIndex2] || null;

                if (player1) round8ForProcess.push(player1);
                if (player2) round8ForProcess.push(player2);
            }

            // gameMatches에서 각 매치의 승자를 찾아서 4강으로 올림
            result.round4 = [];

            // 8강일 때는 첫 번째와 두 번째 팀만 처리 (matchIndex 0, 1)
            const maxMatchIndexForRound4 = playerCount === 8 ? 2 : 4;
            for (let matchIndex = 0; matchIndex < maxMatchIndexForRound4; matchIndex++) {
                const player1 = round8ForProcess[matchIndex * 2];
                const player2 = round8ForProcess[matchIndex * 2 + 1];

                if (!player1 && !player2) continue;

                // gameMatches에서 이 매치의 결과 찾기
                let foundMatch = null;

                if (player1 && player2) {
                    // 두 플레이어가 실제로 게임을 했는지 확인
                    for (const match of gameMatches) {
                        if (match && match.players && match.players.length >= 2) {
                            const hasPlayer1 = match.players.some(p => p.userId === player1.userId);
                            const hasPlayer2 = match.players.some(p => p.userId === player2.userId);

                            if (hasPlayer1 && hasPlayer2 && match.winner) {
                                // 이 매치의 게임 결과 찾음
                                foundMatch = match;
                                break;
                            }
                        }
                    }

                    // 게임이 진행된 경우에만 승자를 4강으로 올림
                    if (foundMatch && foundMatch.winner) {
                        // 4강 매치 인덱스: 8강 매치를 2개씩 묶어서 4강 매치 구성
                        // 8강 매치0,1 → 4강 매치0
                        // 8강 매치2,3 → 4강 매치1
                        const round4MatchIndex = Math.floor(matchIndex / 2);
                        // 4강 매치 내 위치: 8강 매치가 짝수면 첫 번째(0), 홀수면 두 번째(1)
                        const round4Position = matchIndex % 2;
                        const round4SlotIndex = round4MatchIndex * 2 + round4Position;

                        // 승자만 4강에 추가 (타입 변환하여 비교)
                        // winnerIndex는 설정하지 않음 (4강 게임이 아직 진행되지 않았으므로)
                        const winnerId = String(foundMatch.winner);
                        const player1Id = String(player1.userId);
                        const player2Id = String(player2.userId);

                        if (winnerId === player1Id) {
                            result.round4[round4SlotIndex] = {
                                ...player1,
                                bracketPosition: round4SlotIndex,
                                round: 'round4',
                                matchIndex: round4MatchIndex,
                                position: round4Position
                                // winnerIndex는 설정하지 않음 (4강 게임이 아직 진행되지 않았으므로)
                            };
                        } else if (winnerId === player2Id) {
                            result.round4[round4SlotIndex] = {
                                ...player2,
                                bracketPosition: round4SlotIndex,
                                round: 'round4',
                                matchIndex: round4MatchIndex,
                                position: round4Position
                                // winnerIndex는 설정하지 않음 (4강 게임이 아직 진행되지 않았으므로)
                            };
                        }
                    }
                    // 게임이 진행되지 않았으면 4강 슬롯을 생성하지 않음 (빈 슬롯)
                    // 8강 경기를 아직 하지 않았으면 4강에 플레이어를 올리지 않음
                }
            }

            currentRound = result.round4;
        }

        // 4강이 있으면 결승 생성 (16강, 8강, 4강 시작인 경우)
        // 4강 매치의 승자를 직접 찾아서 결승 슬롯에 추가
        if (result.round4.length > 0 && (playerCount === 16 || playerCount === 8 || playerCount === 4)) {
            result.final = [];

            // 4강 매치 개수: 2개 (각 매치에서 승자 1명씩 → 결승 2명)
            const round4MatchCount = 2;
            for (let matchIndex = 0; matchIndex < round4MatchCount; matchIndex++) {
                const slotIndex1 = matchIndex * 2;
                const slotIndex2 = matchIndex * 2 + 1;
                const player1 = result.round4[slotIndex1] || null;
                const player2 = result.round4[slotIndex2] || null;

                if (!player1 && !player2) continue;

                // gameMatches에서 이 4강 매치의 결과 찾기
                let foundMatch = null;

                if (player1 && player2) {
                    // 두 플레이어가 실제로 게임을 했는지 확인
                    for (const match of gameMatches) {
                        if (match && match.players && match.players.length >= 2) {
                            const hasPlayer1 = match.players.some(p => {
                                const matchPlayerId = String(p.userId);
                                const slotUserId = String(player1.userId);
                                return matchPlayerId === slotUserId;
                            });
                            const hasPlayer2 = match.players.some(p => {
                                const matchPlayerId = String(p.userId);
                                const slotUserId = String(player2.userId);
                                return matchPlayerId === slotUserId;
                            });

                            if (hasPlayer1 && hasPlayer2 && match.winner) {
                                // 이 매치의 게임 결과 찾음
                                foundMatch = match;
                                break;
                            }
                        }
                    }

                    // 게임이 진행된 경우에만 승자를 결승으로 올림
                    if (foundMatch && foundMatch.winner) {
                        // 결승 슬롯 인덱스: 4강 매치 인덱스가 0이면 결승 슬롯 0, 1이면 결승 슬롯 1
                        const finalSlotIndex = matchIndex;

                        // 승자만 결승에 추가
                        const winnerId = String(foundMatch.winner);
                        const player1Id = String(player1.userId);
                        const player2Id = String(player2.userId);

                        if (winnerId === player1Id) {
                            result.final[finalSlotIndex] = {
                                ...player1,
                                bracketPosition: finalSlotIndex,
                                round: 'final',
                                matchIndex: 0,
                                position: finalSlotIndex
                            };
                            console.log(`4강 매치${matchIndex} 승자 ${player1.name}를 결승 슬롯[${finalSlotIndex}]에 추가`);
                        } else if (winnerId === player2Id) {
                            result.final[finalSlotIndex] = {
                                ...player2,
                                bracketPosition: finalSlotIndex,
                                round: 'final',
                                matchIndex: 0,
                                position: finalSlotIndex
                            };
                            console.log(`4강 매치${matchIndex} 승자 ${player2.name}를 결승 슬롯[${finalSlotIndex}]에 추가`);
                        }
                    }
                    // 게임이 진행되지 않았으면 결승 슬롯을 생성하지 않음 (빈 슬롯)
                }
            }
        }

        console.log('mapTournamentBracket 최종 결과:', {
            round16: result.round16.length,
            round8: result.round8.length,
            round4: result.round4.length,
            final: result.final.length,
            round4Detail: result.round4.map((r, i) => ({
                index: i,
                name: r?.name,
                userId: r?.userId
            }))
        });

        return result;
    };

    // 특정 라인의 승리 여부 확인 함수 (16강 -> 8강)
    const getRound16To8LineProp = (matchIndex, position) => {
        // matchIndex: 0~7 (8강 매치 인덱스)
        // position: 0 또는 1 (매치 내 위치)
        // 8강 매치 matchIndex의 상단(position 0) = 16강 매치 (matchIndex * 2)의 승자
        // 8강 매치 matchIndex의 하단(position 1) = 16강 매치 (matchIndex * 2 + 1)의 승자
        // 예: 8강 매치0의 상단 = 16강 매치0의 승자, 하단 = 16강 매치1의 승자
        const round16MatchIndex = matchIndex * 2 + position;

        // 16강 매치의 두 플레이어 찾기
        const round16SlotIndex1 = round16MatchIndex * 2; // 매치의 첫 번째 플레이어
        const round16SlotIndex2 = round16MatchIndex * 2 + 1; // 매치의 두 번째 플레이어
        const round16Slot1 = tournamentBracket.round16[round16SlotIndex1];
        const round16Slot2 = tournamentBracket.round16[round16SlotIndex2];

        if (!round16Slot1 || !round16Slot2 || !round16Slot1.userId || !round16Slot2.userId) {
            return undefined;
        }

        // gameMatches에서 해당 16강 매치의 승자 확인
        const gameMatches = window.currentGameMatches || [];

        for (const match of gameMatches) {
            if (match && match.players && match.players.length >= 2 && match.winner) {
                const hasPlayer1 = match.players.some(p => p.userId === round16Slot1.userId);
                const hasPlayer2 = match.players.some(p => p.userId === round16Slot2.userId);

                if (hasPlayer1 && hasPlayer2) {
                    // 같은 16강 매치를 찾음
                    // match.winner는 userId 숫자 또는 문자열일 수 있으므로 타입 변환
                    const winnerId = String(match.winner);

                    // 승자가 첫 번째 플레이어인지 두 번째 플레이어인지 확인
                    const slot1UserId = String(round16Slot1.userId);
                    const slot2UserId = String(round16Slot2.userId);

                    // position에 따라 어떤 플레이어의 연결선을 확인하는지 결정
                    // position이 0이면 첫 번째 플레이어의 연결선, 1이면 두 번째 플레이어의 연결선
                    // 하지만 실제로는 승자의 연결선만 빨간색이어야 함
                    if (position === 0) {
                        // 위쪽 연결선: 첫 번째 플레이어가 승자이면 빨간색
                        if (winnerId === slot1UserId) {
                            return "win 01";
                        }
                    } else if (position === 1) {
                        // 아래쪽 연결선: 두 번째 플레이어가 승자이면 빨간색
                        // 아래쪽 연결선은 항상 "win 01"을 사용 (prop1="아래"와 함께)
                        if (winnerId === slot2UserId) {
                            return "win 01";
                        }
                    }
                    // 승자가 아니면 빨간색 표시하지 않음
                    break;
                }
            }
        }

        return undefined;
    };

    // 특정 라인의 승리 여부 확인 함수 (8강 -> 4강)
    const getRound8To4LineProp = (matchIndex, position) => {
        // matchIndex: 실제로는 4강 매치 인덱스가 아니라 round8SlotIndex를 계산하기 위한 값
        // 렌더링 코드: getRound8To4LineProp(0, 0) = round8[0] -> round4[0]
        //            getRound8To4LineProp(0, 1) = round8[1] -> round4[0]
        //            getRound8To4LineProp(2, 0) = round8[4] -> round4[2]
        //            getRound8To4LineProp(2, 1) = round8[5] -> round4[2]
        // 패턴: round8SlotIndex = matchIndex * 2 + position
        const round8SlotIndex = matchIndex * 2 + position;

        const round8Slot = tournamentBracket.round8[round8SlotIndex];

        if (!round8Slot || !round8Slot.userId) {
            console.log(`getRound8To4LineProp(${matchIndex}, ${position}): round8Slot[${round8SlotIndex}]가 없음`);
            return undefined;
        }

        // 8강일 때 3번째 팀(round8[4], round8[5])과 4번째 팀(round8[6], round8[7])의 경기가 아직 진행되지 않았으면 연결선 없음
        const playerCount = window.currentPlayerCount || 16;
        if (playerCount === 8 && round8SlotIndex >= 4) {
            // 8강일 때 3번째, 4번째 팀 슬롯이 비어있으면 연결선 없음
            return undefined;
        }

        // 해당 슬롯이 속한 8강 매치 찾기 (round8SlotIndex와 round8SlotIndex+1이 같은 매치)
        const round8MatchIndex = Math.floor(round8SlotIndex / 2);
        const round8SlotIndex1 = round8MatchIndex * 2; // 매치의 첫 번째 플레이어
        const round8SlotIndex2 = round8MatchIndex * 2 + 1; // 매치의 두 번째 플레이어
        const round8Slot1 = tournamentBracket.round8[round8SlotIndex1];
        const round8Slot2 = tournamentBracket.round8[round8SlotIndex2];

        if (!round8Slot1 || !round8Slot2 || !round8Slot1.userId || !round8Slot2.userId) {
            console.log(`getRound8To4LineProp(${matchIndex}, ${position}): round8Match[${round8MatchIndex}]의 플레이어가 없음`);
            return undefined;
        }

        // gameMatches에서 해당 8강 매치의 승자 확인
        const gameMatches = window.currentGameMatches || [];
        for (const match of gameMatches) {
            if (match && match.players && match.players.length >= 2 && match.winner) {
                const hasPlayer1 = match.players.some(p => {
                    const matchPlayerId = String(p.userId);
                    const slotUserId = String(round8Slot1.userId);
                    return matchPlayerId === slotUserId;
                });
                const hasPlayer2 = match.players.some(p => {
                    const matchPlayerId = String(p.userId);
                    const slotUserId = String(round8Slot2.userId);
                    return matchPlayerId === slotUserId;
                });

                if (hasPlayer1 && hasPlayer2) {
                    // 같은 8강 매치를 찾음
                    // 현재 확인 중인 연결선이 승자의 연결선인지 확인
                    const winnerId = String(match.winner);
                    const slotUserId = String(round8Slot.userId);

                    console.log(`getRound8To4LineProp(${matchIndex}, ${position}): round8Slot[${round8SlotIndex}]=${round8Slot.name}, winnerId=${winnerId}, slotUserId=${slotUserId}`);

                    if (winnerId === slotUserId) {
                        // 승자의 연결선이므로 빨간색 표시
                        // position에 따라 "win 01" 또는 "win 02" 반환
                        // position이 0이면 위쪽, 1이면 아래쪽
                        console.log(`getRound8To4LineProp(${matchIndex}, ${position}): 승자 매칭 성공, 반환: ${position === 0 ? "win 01" : "win 02"}`);
                        return position === 0 ? "win 01" : "win 02";
                    }
                    break;
                }
            }
        }

        console.log(`getRound8To4LineProp(${matchIndex}, ${position}): 매치를 찾지 못함`);
        return undefined;
    };

    // 특정 라인의 승리 여부 확인 함수 (4강 -> 결승)
    const getRound4ToFinalLineProp = (matchIndex, position) => {
        // matchIndex: 0 (결승 매치 인덱스)
        // position: 0 또는 1 (매치 내 위치)
        // 주석 기준: getRound4ToFinalLineProp(1, 0) = round4[2] -> final[1]
        // 패턴: round4SlotIndex = matchIndex * 2 + position
        const round4SlotIndex = matchIndex * 2 + position;
        const round4Slot = tournamentBracket.round4[round4SlotIndex];

        if (!round4Slot || !round4Slot.userId) {
            console.log(`getRound4ToFinalLineProp(${matchIndex}, ${position}): round4Slot[${round4SlotIndex}]가 없음`);
            return undefined;
        }

        // 해당 슬롯이 속한 4강 매치 찾기 (round4SlotIndex와 round4SlotIndex+1이 같은 매치)
        const round4MatchIndex = Math.floor(round4SlotIndex / 2);
        const round4SlotIndex1 = round4MatchIndex * 2; // 매치의 첫 번째 플레이어
        const round4SlotIndex2 = round4MatchIndex * 2 + 1; // 매치의 두 번째 플레이어
        const round4Slot1 = tournamentBracket.round4[round4SlotIndex1];
        const round4Slot2 = tournamentBracket.round4[round4SlotIndex2];

        if (!round4Slot1 || !round4Slot2 || !round4Slot1.userId || !round4Slot2.userId) {
            console.log(`getRound4ToFinalLineProp(${matchIndex}, ${position}): round4Match[${round4MatchIndex}]의 플레이어가 없음`);
            return undefined;
        }

        // gameMatches에서 해당 4강 매치의 승자 확인
        const gameMatches = window.currentGameMatches || [];
        for (const match of gameMatches) {
            if (match && match.players && match.players.length >= 2 && match.winner) {
                const hasPlayer1 = match.players.some(p => String(p.userId) === String(round4Slot1.userId));
                const hasPlayer2 = match.players.some(p => String(p.userId) === String(round4Slot2.userId));

                if (hasPlayer1 && hasPlayer2) {
                    // 같은 4강 매치를 찾음
                    // 현재 확인 중인 연결선이 승자의 연결선인지 확인
                    const winnerId = String(match.winner);
                    const slotUserId = String(round4Slot.userId);

                    console.log(`getRound4ToFinalLineProp(${matchIndex}, ${position}): round4Slot[${round4SlotIndex}]=${round4Slot.name}, winnerId=${winnerId}, slotUserId=${slotUserId}`);

                    if (winnerId === slotUserId) {
                        // 승자의 연결선이므로 빨간색 표시
                        // position에 따라 "win 01" 또는 "win 02" 반환
                        console.log(`getRound4ToFinalLineProp(${matchIndex}, ${position}): 승자 매칭 성공, 반환: ${position === 0 ? "win 01" : "win 02"}`);
                        return position === 0 ? "win 01" : "win 02";
                    }
                    break;
                }
            }
        }

        console.log(`getRound4ToFinalLineProp(${matchIndex}, ${position}): 매치를 찾지 못함`);
        return undefined;
    };

    // 특정 라인의 승리 여부 확인 함수 (결승 -> 챔피언)
    const getFinalToChampionLineProp = () => {
        // 결승은 final[0] vs final[1] 하나의 매치만 있음
        const finalSlot0 = tournamentBracket.final[0];
        const finalSlot1 = tournamentBracket.final[1];

        if (!finalSlot0 || !finalSlot1 || !finalSlot0.userId || !finalSlot1.userId) {
            console.log('getFinalToChampionLineProp: final 슬롯이 없음');
            return undefined;
        }

        // gameMatches에서 결승 매치의 승자 확인
        const gameMatches = window.currentGameMatches || [];
        for (const match of gameMatches) {
            if (match && match.players && match.players.length >= 2 && match.winner) {
                const hasPlayer0 = match.players.some(p => String(p.userId) === String(finalSlot0.userId));
                const hasPlayer1 = match.players.some(p => String(p.userId) === String(finalSlot1.userId));

                if (hasPlayer0 && hasPlayer1) {
                    // 결승 매치를 찾음
                    const winnerId = String(match.winner);
                    const slot0Id = String(finalSlot0.userId);
                    const slot1Id = String(finalSlot1.userId);

                    console.log(`getFinalToChampionLineProp: winnerId=${winnerId}, slot0Id=${slot0Id}, slot1Id=${slot1Id}`);

                    // 승자가 final[0]이면 "win 01", final[1]이면 "win 02" 반환
                    if (winnerId === slot0Id) {
                        console.log('getFinalToChampionLineProp: 반환 win 01');
                        return "win 01";
                    } else if (winnerId === slot1Id) {
                        console.log('getFinalToChampionLineProp: 반환 win 02');
                        return "win 02";
                    }
                    break;
                }
            }
        }

        console.log('getFinalToChampionLineProp: 매치를 찾지 못함');
        return undefined;
    };

    // 챔피언 이름 가져오기 함수
    const getChampionName = () => {
        const finalSlot0 = tournamentBracket.final[0];
        const finalSlot1 = tournamentBracket.final[1];

        if (!finalSlot0 || !finalSlot1 || !finalSlot0.userId || !finalSlot1.userId) {
            return t('champion');
        }

        // gameMatches에서 결승 매치의 승자 확인
        const gameMatches = window.currentGameMatches || [];
        for (const match of gameMatches) {
            if (match && match.players && match.players.length >= 2 && match.winner) {
                const hasPlayer0 = match.players.some(p => String(p.userId) === String(finalSlot0.userId));
                const hasPlayer1 = match.players.some(p => String(p.userId) === String(finalSlot1.userId));

                if (hasPlayer0 && hasPlayer1) {
                    // 결승 매치를 찾음
                    const winnerId = String(match.winner);
                    const slot0Id = String(finalSlot0.userId);
                    const slot1Id = String(finalSlot1.userId);

                    // 승자 이름 반환
                    if (winnerId === slot0Id) {
                        return finalSlot0.name || t('champion');
                    } else if (winnerId === slot1Id) {
                        return finalSlot1.name || t('champion');
                    }
                    break;
                }
            }
        }

        return t('champion');
    };

    // 현재까지 진행된 최상위 라운드 확인
    const getHighestRound = () => {
        if (tournamentBracket.final.length > 0 && tournamentBracket.final.some(s => s && s.name)) {
            return 'final';
        } else if (tournamentBracket.round4.length > 0 && tournamentBracket.round4.some(s => s && s.name)) {
            return 'round4';
        } else if (tournamentBracket.round8.length > 0 && tournamentBracket.round8.some(s => s && s.name)) {
            return 'round8';
        } else if (tournamentBracket.round16.length > 0 && tournamentBracket.round16.some(s => s && s.name)) {
            return 'round16';
        }
        return null;
    };

    // 플레이어가 최상위 라운드에 있는지 확인
    const isPlayerInHighestRound = (userId) => {
        const highestRound = getHighestRound();
        if (!highestRound) return false;

        const highestRoundData = tournamentBracket[highestRound];
        if (!highestRoundData) return false;

        // 최상위 라운드에 해당 userId가 있는지 확인
        return highestRoundData.some(slot => slot && slot.userId === userId);
    };

    // 특정 라운드의 플레이어가 해당 라운드에서 승자인지 확인
    const isWinnerInRound = (slot, round) => {
        if (!slot || slot.matchIndex === undefined) {
            return false;
        }

        const roundData = tournamentBracket[round];
        if (roundData && roundData[slot.matchIndex]) {
            const matchWinner = roundData[slot.matchIndex];
            // matchWinner의 userId와 slot의 userId를 비교
            return matchWinner.userId === slot.userId;
        }
        return false;
    };

    // 슬롯 상태 결정 함수
    const getSlotStatus = (slot, round, matchIndex, position, playerCount, gameMatches = []) => {
        // 가장 하위 라운드인지 확인
        const isFirstRound =
            (playerCount === 16 && round === 'round16') ||
            (playerCount === 8 && round === 'round8') ||
            (playerCount === 4 && round === 'round4') ||
            (playerCount === 2 && round === 'final');

        const highestRound = getHighestRound();

        if (isFirstRound) {
            // 가장 하위 라운드 슬롯
            if (!slot || !slot.name) {
                return '등록전';
            }

            // 게임이 진행되었는지 확인
            // 해당 매치에서 slot이 플레이했는지 확인
            let foundMatch = null;
            for (const match of gameMatches) {
                if (match && match.players && match.players.length >= 2) {
                    const playerIds = match.players.map(p => p.userId);
                    // 같은 매치에 slot이 포함되어 있는지 확인
                    // matchIndex를 직접 사용하기보다는, 실제 게임 매칭에서 찾아야 함
                    // 하지만 하위 라운드의 경우 matchIndex가 정확히 매칭될 수도 있음
                    const isInMatch = playerIds.includes(slot.userId);
                    if (isInMatch) {
                        // 같은 게임의 다른 플레이어도 같은 round, 같은 matchIndex에 있어야 함
                        // 이를 더 정확히 확인하려면 bracketPosition이나 다른 방법이 필요하지만,
                        // 우선 같은 게임에 포함된 매치를 찾음
                        foundMatch = match;
                        break;
                    }
                }
            }

            // matchIndex로 직접 찾기도 시도
            if (!foundMatch && matchIndex < gameMatches.length) {
                const match = gameMatches[matchIndex];
                if (match && match.players) {
                    const playerIds = match.players.map(p => p.userId);
                    if (playerIds.includes(slot.userId)) {
                        foundMatch = match;
                    }
                }
            }

            if (foundMatch && foundMatch.winner) {
                // 게임이 진행되었음
                if (foundMatch.winner === slot.userId) {
                    // 해당 라운드 승자
                    // 플레이어가 최상위 라운드에 있는지 확인
                    if (isPlayerInHighestRound(slot.userId)) {
                        // 최상위 라운드까지 올라간 플레이어는 빨강색
                        return '승리';
                    } else {
                        // 이후 라운드에서 패배한 플레이어는 흐린 빨강
                        return '승리-1';
                    }
                } else if (foundMatch.loser === slot.userId) {
                    // 패자
                    return '패배';
                }
            }
            // 게임이 아직 진행되지 않았음
            return '등록전';
        } else {
            // 상위 라운드 슬롯
            if (!slot || !slot.name) {
                // 플레이어가 결정되지 않았음
                return 'empty';
            }

            // 플레이어가 결정되었음
            // 기본값은 "대기" (노란색)
            let status = '대기';

            // 해당 라운드에서 승자인지 확인
            const roundData = tournamentBracket[round];
            if (roundData && slot.matchIndex !== undefined) {
                // slot.winnerIndex를 확인하여 이 슬롯이 승자인지 판단
                if (slot.winnerIndex !== undefined) {
                    // winnerIndex가 있으면 게임이 진행되었음
                    if (slot.winnerIndex === slot.bracketPosition) {
                        // 이 플레이어가 승자
                        // 플레이어가 아직 살아남아있는지 확인 (최상위 라운드에 있는지)
                        if (isPlayerInHighestRound(slot.userId)) {
                            // 아직 살아남아있음 → 빨강색
                            status = '승리';
                        } else {
                            // 이후에 탈락함 → 흐린 빨강
                            status = '승리-1';
                        }
                    } else {
                        // 같은 매치에 다른 플레이어가 승자
                        // 패자 → 어두운 회색
                        status = '패배';
                    }
                }
                // winnerIndex가 없으면 게임이 아직 진행되지 않았으므로 "대기" 유지
            }

            return status;
        }
    };

    // 슬롯의 표시 텍스트 결정 함수 (prop이 '등록전'이면 '등록' 반환)
    const getSlotText = (slot, prop) => {
        if (prop === '등록전') {
            return t('registration');
        }
        return slot?.name || '';
    };

    // 챔피언십 랭킹 데이터 가져오기
    useEffect(() => {
        const fetchChampionRanks = async () => {
            if (!championshipData?.id) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                let ranks = [];
                let gameMatches = []; // 게임 매칭 정보 저장용

                // 토너먼트인 경우 playLogs에서 가져오기 (100개씩 페이지네이션)
                if (championshipData?.type === 1) {
                    const playLogsParams = {
                        championId: championshipData.id,
                        state: 0,
                        expand: 'user',
                    };

                    const allLogs = [];
                    const limit = API_PAGE_SIZE;
                    let offset = 0;
                    let hasMore = true;

                    while (hasMore) {
                        const playLogsResponse = await logAPI.getPlayLogs({
                            ...playLogsParams,
                            limit,
                            offset,
                        });
                        const pageLogs = playLogsResponse?.data?.logs || playLogsResponse?.logs || playLogsResponse?.data || [];
                        const logsArray = Array.isArray(pageLogs) ? pageLogs : [];
                        allLogs.push(...logsArray);
                        if (logsArray.length < limit) {
                            hasMore = false;
                        } else {
                            offset += limit;
                        }
                    }

                    // playLogs 응답에서 랭킹 데이터 추출
                    const logs = allLogs;

                    // 같은 게임을 그룹화 (startTime, endTime이 동일한 것들을 같은 게임으로 처리)
                    const gameMap = new Map();
                    logs.forEach(log => {
                        if (!log.userId || !log.startTime || !log.endTime) return;

                        const gameKey = `${log.startTime}_${log.endTime}`;
                        if (!gameMap.has(gameKey)) {
                            gameMap.set(gameKey, []);
                        }
                        gameMap.get(gameKey).push(log);
                    });

                    // 게임들을 시간순으로 정렬 (가장 먼저 플레이한 게임이 앞에 오도록)
                    const sortedGames = Array.from(gameMap.entries()).sort((a, b) => {
                        const timeA = new Date(a[0].split('_')[0]).getTime();
                        const timeB = new Date(b[0].split('_')[0]).getTime();
                        return timeA - timeB; // 오래된 게임이 먼저
                    });

                    // 사용자별 승리 횟수 집계 및 사용자 정보 저장
                    const userWinMap = new Map();
                    const userInfoMap = new Map();
                    gameMatches = []; // 게임 매칭 정보 저장 (시간순)

                    sortedGames.forEach(([gameKey, gameLogs]) => {
                        const match = {
                            startTime: gameKey.split('_')[0],
                            players: [],
                            winner: null,
                            loser: null
                        };

                        // 각 게임에서 isWin === 1인 유저가 승리
                        gameLogs.forEach(log => {
                            const userId = log.userId;
                            if (!userId) return;

                            // 사용자 정보 저장 (logs에 expand: 'user'로 포함됨)
                            if (!userInfoMap.has(userId) && log.user) {
                                userInfoMap.set(userId, log.user);
                            }

                            if (!userWinMap.has(userId)) {
                                userWinMap.set(userId, {
                                    userId: userId,
                                    wincount: 0,
                                });
                            }

                            // 매칭 정보에 플레이어 추가
                            match.players.push({
                                userId: userId,
                                isWin: log.isWin === 1
                            });

                            // isWin이 1인 경우 승리
                            if (log.isWin === 1) {
                                userWinMap.get(userId).wincount += 1;
                                match.winner = userId;
                            } else {
                                match.loser = userId;
                            }
                        });

                        gameMatches.push(match);
                    });

                    // 유저 정보가 없는 경우 getUsers API 호출
                    const userIds = Array.from(userWinMap.keys());
                    const missingUserIds = userIds.filter(userId => !userInfoMap.has(userId));

                    if (missingUserIds.length > 0) {
                        try {
                            const limit = API_PAGE_SIZE;
                            let offset = 0;
                            let hasMore = true;

                            while (hasMore) {
                                const usersResponse = await userAPI.getUsers('all', { state: 0, limit, offset });
                                const users = usersResponse?.data?.users || usersResponse?.users || [];
                                const usersArray = Array.isArray(users) ? users : [];

                                usersArray.forEach(user => {
                                    if (missingUserIds.includes(user.id)) {
                                        userInfoMap.set(user.id, user);
                                    }
                                });

                                if (usersArray.length < limit) {
                                    hasMore = false;
                                } else {
                                    offset += limit;
                                }
                            }
                        } catch (error) {
                            console.error('사용자 정보 가져오기 실패:', error);
                        }
                    }

                    // 랭킹 데이터 생성 (게임 매칭 순서대로 배치)
                    // gameMatches를 기반으로 사용자를 시간순으로 배치
                    const playerSet = new Set();
                    const orderedRanks = [];

                    // 게임 매칭 순서대로 플레이어 추가 (가장 먼저 플레이한 게임이 위로)
                    gameMatches.forEach(match => {
                        match.players.forEach(player => {
                            if (!playerSet.has(player.userId)) {
                                const userWinData = userWinMap.get(player.userId);
                                const userInfo = userInfoMap.get(player.userId) || {};

                                orderedRanks.push({
                                    userId: player.userId,
                                    name: userInfo.name || t('user'),
                                    nickname: userInfo.nickname || '',
                                    profilePath: userInfo.profilePath || '',
                                    score: '0', // 토너먼트는 승리 횟수 기준
                                    wincount: userWinData.wincount || 0,
                                });
                                playerSet.add(player.userId);
                            }
                        });
                    });

                    // 매칭에 포함되지 않은 사용자 추가 (승리 횟수 기준으로 정렬)
                    const unmatchedUsers = userIds.filter(userId => !playerSet.has(userId));
                    const unmatchedRanks = unmatchedUsers.map(userId => {
                        const userWinData = userWinMap.get(userId);
                        const userInfo = userInfoMap.get(userId) || {};

                        return {
                            userId: userId,
                            name: userInfo.name || t('user'),
                            nickname: userInfo.nickname || '',
                            profilePath: userInfo.profilePath || '',
                            score: '0',
                            wincount: userWinData.wincount || 0,
                        };
                    }).sort((a, b) => (b.wincount || 0) - (a.wincount || 0));

                    ranks = [...orderedRanks, ...unmatchedRanks];

                } else {
                    // 랭킹 타입인 경우 getChampionRanks 사용 (API_PAGE_SIZE씩 페이지네이션)
                    const allRanks = [];
                    const limit = API_PAGE_SIZE;
                    let offset = 0;
                    let hasMore = true;

                    while (hasMore) {
                        const response = await rankAPI.getChampionRanks({
                            championId: championshipData.id,
                            type: championshipData.type,
                            limit,
                            offset,
                        });
                        const pageRanks = response?.data?.ranks || response?.ranks || response?.data || [];
                        const ranksArray = Array.isArray(pageRanks) ? pageRanks : [];
                        allRanks.push(...ranksArray);
                        if (ranksArray.length < limit) {
                            hasMore = false;
                        } else {
                            offset += limit;
                        }
                    }
                    ranks = allRanks;
                }

                // 랭킹 데이터 변환
                const formattedRanks = ranks.map((item, index) => {
                    const userId = item.userId;
                    const name = item.name;
                    const score = item.score || '0';
                    const wincount = item.wincount || item.winCount || 0;
                    const currentUserId = meInfo?.id;

                    return {
                        rank: item.rank || index + 1,
                        userId,
                        name: name,
                        score: score.toString(),
                        wincount,
                        isCurrentUser: userId === currentUserId,
                    };
                });

                // 랭킹 타입인 경우
                if (championshipData?.type === 0) {
                    setRankingData(formattedRanks);
                }
                // 토너먼트 타입인 경우
                else if (championshipData?.type === 1) {
                    const playerCount = championshipData?.playerCount || 16;
                    // gameMatches 정보를 브래킷 매핑에 전달
                    const bracket = mapTournamentBracket(formattedRanks, playerCount, gameMatches || []);
                    console.log('setTournamentBracket 전 bracket.round4:', bracket.round4);
                    console.log('setTournamentBracket 전 bracket.round4[0]:', bracket.round4[0]);
                    console.log('setTournamentBracket 전 bracket.round4[1]:', bracket.round4[1]);
                    setTournamentBracket(bracket);
                    // gameMatches를 전역으로 저장 (슬롯 상태 확인에 사용)
                    window.currentGameMatches = gameMatches || [];
                    window.currentPlayerCount = playerCount;
                    // bracket을 전역으로도 저장하여 렌더링 시 확인 가능하도록
                    window.currentBracket = bracket;
                }
            } catch (error) {
                console.error('Failed to fetch champion ranks:', error);
                setRankingData([]);
                setTournamentBracket({ round16: [], round8: [], round4: [], final: [] });
            } finally {
                setLoading(false);
            }
        };

        fetchChampionRanks();
    }, [championshipData?.id, championshipData?.type, championshipData?.startDate, championshipData?.endDate, meInfo?.id]);

    return (
        <>
            {championshipData && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    px: 2,
                    zIndex: 1,
                    flexShrink: 0
                }}>
                    <Box sx={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        border: '2px solid ' + theme.palette.figma.iconWhite,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {championshipData.icon ? (
                            <img
                                src={championshipData.icon}
                                alt={championshipData.contentName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <ContentsIconDefault />
                        )}
                    </Box>
                    <Typography sx={{
                        color: theme.palette.figma.white[100],
                        fontSize: '18px',
                        fontWeight: 600,
                        textAlign: 'center'
                    }}>
                        {championshipData.contentName}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        px: '10px',
                        py: '5px',
                        backgroundColor: theme.palette.figma.white[20],
                        borderRadius: '10px'
                    }}>
                        {championshipData.type === 0 ? (
                            <>
                                <Ranking />
                                <Typography sx={{
                                    color: '#f9a0ff',
                                    fontSize: '12px',
                                    fontWeight: 500
                                }}>
                                    {t('ranking')}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Tournament />
                                <Typography sx={{
                                    color: '#81d9ff',
                                    fontSize: '12px',
                                    fontWeight: 500
                                }}>
                                    {t('tournament')}
                                </Typography>
                            </>
                        )}
                    </Box>
                    <Box sx={{
                        backgroundColor: theme.palette.figma.white[10],
                        borderRadius: '15px',
                        padding: '10px',
                        textAlign: 'center'
                    }}>
                        <Typography sx={{
                            color: theme.palette.figma.yellowText,
                            fontSize: '16px',
                            fontWeight: 600,
                            marginBottom: '3px'
                        }}>
                            {championshipData.name}
                        </Typography>
                        <Typography sx={{
                            color: theme.palette.figma.iconWhite,
                            fontSize: '12px',
                            opacity: 0.6
                        }}>
                            {championshipData.startDate && championshipData.endDate
                                ? `${formatDateTimeDisplay(championshipData.startDate, 'date')} ~ ${formatDateTimeDisplay(championshipData.endDate, 'date')}`
                                : championshipData.endDate ? formatDateTimeDisplay(championshipData.endDate, 'date') : ''
                            }
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Ranking Section - 랭킹일 때만 표시 */}
            {
                championshipData?.type === 0 ? (
                    loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : rankingData.length === 0 ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                            // minHeight: '300px'
                        }}>
                            <Typography sx={{
                                color: theme.palette.figma.white[100],
                                fontSize: '16px',
                                fontWeight: 500,
                                textAlign: 'center'
                            }}>
                                {t('no_participants')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            width: '100%',
                            px: '20px',
                        }}>
                            {/* 상위 3명 포디움 */}
                            {rankingData.length >= 3 && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'end',
                                    justifyContent: 'center',
                                    height: '120px'
                                }}>
                                    {/* 2위 */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'start',
                                        height: '100px',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '15px 15px 0 0',
                                        position: 'relative',
                                        background: rankingData[1]?.isCurrentUser
                                            ? 'linear-gradient(to bottom, #ff6c02, #040116)'
                                            : 'linear-gradient(to bottom, #b3b3b3, #040116)',
                                        boxShadow: 'inset 0px 4px 4px 0px rgba(255,255,255,0.5)',
                                        border: rankingData[1]?.isCurrentUser ? '2px solid #ff6c02' : 'none'
                                    }}>
                                        <Typography sx={{
                                            fontSize: '25px',
                                            fontWeight: 600,
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center',
                                            marginBottom: '3px'
                                        }}>
                                            2
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center',
                                            marginBottom: '3px'
                                        }}>
                                            {rankingData[1]?.name || '-'}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center'
                                        }}>
                                            {rankingData[1]?.score || '-'}
                                        </Typography>
                                    </Box>

                                    {/* 1위 */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'start',
                                        height: '120px',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '15px 15px 0 0',
                                        position: 'relative',
                                        background: rankingData[0]?.isCurrentUser
                                            ? 'linear-gradient(to bottom, #ff6c02, #040116)'
                                            : 'linear-gradient(to bottom, #ffb700, #040116)',
                                        boxShadow: 'inset 0px 4px 4px 0px rgba(255,255,255,0.5)',
                                        border: rankingData[0]?.isCurrentUser ? '2px solid #ff6c02' : 'none'
                                    }}>
                                        <Typography sx={{
                                            fontSize: '30px',
                                            fontWeight: 600,
                                            color: rankingData[0]?.isCurrentUser
                                                ? theme.palette.figma.buttonText
                                                : theme.palette.figma.yellowText,
                                            textAlign: 'center',
                                            marginBottom: '3px'
                                        }}>
                                            1
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center',
                                            marginBottom: '3px'
                                        }}>
                                            {rankingData[0]?.name || '-'}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center'
                                        }}>
                                            {rankingData[0]?.score || '-'}
                                        </Typography>
                                    </Box>

                                    {/* 3위 */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'start',
                                        height: '90px',
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '15px 15px 0 0',
                                        position: 'relative',
                                        background: rankingData[2]?.isCurrentUser
                                            ? 'linear-gradient(to bottom, #ff6c02, #040116)'
                                            : 'linear-gradient(to bottom, #d2863a, #040116)',
                                        boxShadow: 'inset 0px 4px 4px 0px rgba(255,255,255,0.5)',
                                        border: rankingData[2]?.isCurrentUser ? '2px solid #ff6c02' : 'none'
                                    }}>
                                        <Typography sx={{
                                            fontSize: '20px',
                                            fontWeight: 600,
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center',
                                            marginBottom: '3px'
                                        }}>
                                            3
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center',
                                            marginBottom: '3px'
                                        }}>
                                            {rankingData[2]?.name || '-'}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: theme.palette.figma.buttonText,
                                            textAlign: 'center'
                                        }}>
                                            {rankingData[2]?.score || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                gap: '5px',
                            }}>
                                {/* 랭킹 리스트 - 스크롤 가능 */}
                                <Box sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '5px',
                                    width: '100%',
                                    height: '264px',
                                    overflowY: 'auto',
                                }}>
                                    {rankingData.length > 3 && rankingData.slice(3).map((item, index) => {
                                        // 현재 사용자인지 확인
                                        const isCurrentUser = item.isCurrentUser;

                                        return (
                                            <Box
                                                key={item.userId || index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '10px',
                                                    borderRadius: '10px',
                                                    backgroundColor: isCurrentUser
                                                        ? theme.palette.figma.buttonOrange
                                                        : theme.palette.figma.block,
                                                    width: '100%',
                                                    flexShrink: 0,
                                                    height: '37px'
                                                }}
                                            >
                                                <Box sx={{ width: '60px', textAlign: 'center' }}>
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: isCurrentUser
                                                            ? theme.palette.figma.buttonText
                                                            : theme.palette.figma.white[80],
                                                        textAlign: 'center'
                                                    }}>
                                                        {item.rank}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: isCurrentUser
                                                            ? theme.palette.figma.buttonText
                                                            : theme.palette.figma.white[80],
                                                        textAlign: 'center'
                                                    }}>
                                                        {item.name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: isCurrentUser
                                                            ? theme.palette.figma.buttonText
                                                            : theme.palette.figma.white[80],
                                                        textAlign: 'center'
                                                    }}>
                                                        {item.score}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {/* 현재 사용자가 순위권에 없을 때만 맨 아래에 표시 */}
                                {(() => {
                                    const currentUser = rankingData.find(item => item.isCurrentUser);
                                    if (!currentUser) return null;

                                    // 현재 사용자가 이미 순위권(상위 3명) 또는 리스트(4위 이후)에 포함되어 있는지 확인
                                    // 상위 3명에 있거나, 4위 이후 리스트에 있으면 이미 표시되고 있음
                                    const isInTop3 = currentUser.rank >= 1 && currentUser.rank <= 3;
                                    const isInList = currentUser.rank > 3 && currentUser.rank <= rankingData.length;

                                    // 순위권에 포함되어 있으면 맨 아래에 표시하지 않음
                                    if (isInTop3 || isInList) return null;

                                    return (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px',
                                            borderRadius: '10px',
                                            backgroundColor: theme.palette.figma.buttonOrange,
                                            width: '100%',
                                            flexShrink: 0
                                        }}>
                                            <Box sx={{ width: '60px', textAlign: 'center' }}>
                                                <Typography sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: theme.palette.figma.buttonText,
                                                    textAlign: 'center'
                                                }}>
                                                    {currentUser.rank}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                <Typography sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: theme.palette.figma.buttonText,
                                                    textAlign: 'center'
                                                }}>
                                                    {currentUser.name}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                <Typography sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: theme.palette.figma.buttonText,
                                                    textAlign: 'center'
                                                }}>
                                                    {currentUser.score}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })()}
                            </Box>
                        </Box>
                    )
                )
                    : loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (() => {
                        // 토너먼트 브래킷에 참가자가 있는지 확인
                        const hasPlayers =
                            (tournamentBracket.round16 && tournamentBracket.round16.some(slot => slot && slot.name)) ||
                            (tournamentBracket.round8 && tournamentBracket.round8.some(slot => slot && slot.name)) ||
                            (tournamentBracket.round4 && tournamentBracket.round4.some(slot => slot && slot.name)) ||
                            (tournamentBracket.final && tournamentBracket.final.some(slot => slot && slot.name));

                        if (!hasPlayers) {
                            return (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flex: 1,
                                    minHeight: '300px'
                                }}>
                                    <Typography sx={{
                                        color: theme.palette.figma.white[100],
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        textAlign: 'center'
                                    }}>
                                        {t('no_participants')}
                                    </Typography>
                                </Box>
                            );
                        }

                        return (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                marginTop: window.currentPlayerCount === 8 ? '-52.5px' : window.currentPlayerCount === 4 ? '-135px' : window.currentPlayerCount === 2 ? '-310px' : '0px',
                                marginLeft: window.currentPlayerCount === 8 ? '-64px' : window.currentPlayerCount === 4 ? '-128px' : window.currentPlayerCount === 2 ? '-192px' : '-14px',
                                width: '100%',
                                flex: 1,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                zIndex: 1
                            }}>
                                {/* 토너먼트 브래킷 - playerCount에 따라 동적으로 렌더링 */}
                                <Box sx={{
                                    position: 'relative',
                                    width: '100%',
                                    minHeight: '734px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    paddingBottom: '20px'
                                }}>
                                    {(() => {
                                        // playerCount를 숫자로 변환하여 비교
                                        const playerCount = Number(championshipData?.playerCount) || 16;

                                        // 디버깅: playerCount 확인
                                        console.log('토너먼트 렌더링 - playerCount:', playerCount, 'type:', typeof playerCount);
                                        console.log('championshipData?.playerCount:', championshipData?.playerCount);
                                        console.log('championshipData:', championshipData);

                                        // 모든 경우에 16강 레이아웃 사용
                                        return (
                                            <>
                                                {/* 토너먼트 라인들 */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    width: '100%',
                                                    height: '100%',
                                                    zIndex: 1,
                                                }}>
                                                    {/* 첫 번째 토너먼트 브래킷 라인들 */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: '106px',
                                                        top: '35.5px',
                                                    }}>
                                                        {/* TournamentLine - round16[0] -> round8[0] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '0px',
                                                                width: '22px',
                                                                height: '42px',
                                                            }}>
                                                                <TournamentLine prop={getRound16To8LineProp(0, 0) || undefined} />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine2 - round8[0] -> round4[0] */}
                                                        {(playerCount === 16 || playerCount === 8) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '64px',
                                                                top: '53px',
                                                                width: '22px',
                                                                height: '68px'
                                                            }}>
                                                                <TournamentLine2 prop={getRound8To4LineProp(0, 0) || undefined} />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine3 - round4[0] -> final[0] */}
                                                        {(playerCount === 16 || playerCount === 8 || playerCount === 4) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '84px',
                                                                top: '133px',
                                                                width: '61px',
                                                                height: '173px'
                                                            }}>
                                                                <TournamentLine3 prop={getRound4ToFinalLineProp(0, 0) || undefined} />
                                                            </Box>
                                                        )}

                                                        {(playerCount === 16 || playerCount === 8) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '64px',
                                                                top: '170px',
                                                                width: '22px',
                                                                height: '68px'
                                                            }}>
                                                                <TournamentLine2
                                                                    prop={getRound8To4LineProp(1, 1) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}


                                                        {/* TournamentLine - round16[2] -> round8[0] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '88px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine
                                                                    prop={getRound16To8LineProp(0, 1) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine - round16[4] -> round8[1] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '160px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine
                                                                    prop={getRound16To8LineProp(1, 0) || undefined}
                                                                />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine - round16[6] -> round8[2] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '248px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine
                                                                    prop={getRound16To8LineProp(2, 1) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {/* 두 번째 토너먼트 브래킷 라인들 */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: '106px',
                                                        top: '391px'
                                                    }}>
                                                        {/* TournamentLine3 - round4[2] -> final[1] */}
                                                        {(playerCount === 16 || playerCount === 8 || playerCount === 4) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '84px',
                                                                top: '0px',
                                                                width: '61px',
                                                                height: '173px'
                                                            }}>
                                                                <TournamentLine3
                                                                    prop={getRound4ToFinalLineProp(1, 1) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}

                                                        {(playerCount === 16 || playerCount === 8) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '64px',
                                                                top: '68px',
                                                                width: '22px',
                                                                height: '68px'
                                                            }}>
                                                                <TournamentLine2 prop={getRound8To4LineProp(2, 2) || undefined} />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine2 - round8[5] -> round4[2] (16강일 때만) 또는 round8[3] -> round4[0] (8강일 때) */}

                                                        {(playerCount === 16 || playerCount === 8) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '64px',
                                                                top: '185px',
                                                                width: '22px',
                                                                height: '68px'
                                                            }}>
                                                                <TournamentLine2
                                                                    prop={getRound8To4LineProp(3, 3) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}



                                                        {/* TournamentLine - round16[8] -> round8[4] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '15px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine prop={getRound16To8LineProp(4, 0) || undefined} />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine - round16[10] -> round8[4] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '103px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine
                                                                    prop={getRound16To8LineProp(4, 1) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine - round16[12] -> round8[5] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '174px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine prop={getRound16To8LineProp(5, 0) || undefined} />
                                                            </Box>
                                                        )}

                                                        {/* TournamentLine - round16[14] -> round8[5] */}
                                                        {playerCount === 16 && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '0px',
                                                                top: '265px',
                                                                width: '22px',
                                                                height: '42px'
                                                            }}>
                                                                <TournamentLine
                                                                    prop={getRound16To8LineProp(5, 1) || undefined}
                                                                    prop1="아래"
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <TournamentLineFinal
                                                        sx={{
                                                            position: 'absolute',
                                                            left: '234px',
                                                            top: '353px',
                                                            width: '22px',
                                                            height: '42px'
                                                        }}
                                                        prop={getFinalToChampionLineProp() || undefined}
                                                    />

                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: '256px',
                                                        top: '353px',
                                                        width: '102.64px',
                                                        height: '29.43px',
                                                        flexShrink: 0,
                                                    }}>
                                                        {/* Champion Banner Background */}
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: '71.2',
                                                            right: 0,
                                                            top: '12.6',
                                                            width: '31.481px',
                                                            height: '16.82px',
                                                            flexShrink: 0
                                                        }}>
                                                            <ChampionBannerRight />
                                                        </Box>

                                                        <Box sx={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: '71.2px',
                                                            top: '12.6',
                                                            width: '31.481px',
                                                            height: '16.82px',
                                                            flexShrink: 0
                                                        }}>
                                                            <ChampionBannerLeft />
                                                        </Box>

                                                        <Box sx={{
                                                            position: 'absolute',
                                                            bottom: '4.43px',
                                                            left: '9.32px',
                                                            right: '9.32px',
                                                            top: 0,
                                                            width: '84px',
                                                            height: '25px',
                                                            flexShrink: 0
                                                        }}>
                                                            <ChampionBannerTop />
                                                        </Box>

                                                        {/* Champion Text */}
                                                        <Typography sx={{
                                                            position: 'absolute',
                                                            top: 'calc(50% - 3.215px)',
                                                            left: 'calc(50% - 0.32px)',
                                                            transform: 'translate(-50%, -50%)',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            color: theme.palette.figma.iconWhite,
                                                            textAlign: 'center',
                                                            lineHeight: 1,
                                                            width: '80px',
                                                            letterSpacing: '-0.42px',
                                                            fontFamily: 'Pretendard, sans-serif'
                                                        }}>
                                                            {getChampionName()}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* 16강~4강 그룹 */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: '20px',
                                                    top: '26px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '60px',
                                                    alignItems: 'flex-end',
                                                }}>
                                                    {/* 첫 번째 16강~4강 그룹 */}
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '20px'
                                                    }}>
                                                        {/* 16강~8강 그룹 */}
                                                        <Box>
                                                            {/* 첫 번째 16강~8강 그룹 */}
                                                            <Box>
                                                                {/* 16강 그룹 */}
                                                                {playerCount === 16 && (
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '10px'
                                                                    }}>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '0px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[0],
                                                                                        'round16',
                                                                                        0,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[0], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[0],
                                                                                    'round16',
                                                                                    0,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[1],
                                                                                        'round16',
                                                                                        0,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[1], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[1],
                                                                                    'round16',
                                                                                    0,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '105px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[2],
                                                                                        'round16',
                                                                                        1,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[2], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[2],
                                                                                    'round16',
                                                                                    1,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[3],
                                                                                        'round16',
                                                                                        1,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[3], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[3],
                                                                                    'round16',
                                                                                    1,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                {/* 8강 */}
                                                                {(playerCount === 16 || playerCount === 8) && (
                                                                    <Box sx={{
                                                                        position: 'absolute',
                                                                        left: '64px',
                                                                        top: '52.5px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '5px'
                                                                    }}>
                                                                        <TournamentSlot
                                                                            text={tournamentBracket.round8[0]?.name || ''}
                                                                            prop={getSlotStatus(
                                                                                tournamentBracket.round8[0],
                                                                                'round8',
                                                                                0,
                                                                                0,
                                                                                window.currentPlayerCount || 16,
                                                                                window.currentGameMatches || []
                                                                            )}
                                                                        />
                                                                        <TournamentSlot
                                                                            text={tournamentBracket.round8[1]?.name || ''}
                                                                            prop={getSlotStatus(
                                                                                tournamentBracket.round8[1],
                                                                                'round8',
                                                                                0,
                                                                                1,
                                                                                window.currentPlayerCount || 16,
                                                                                window.currentGameMatches || []
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                )}

                                                            </Box>
                                                            {/* 두 번째 16강~8강 그룹 */}
                                                            <Box>
                                                                {/* 16강 그룹 */}
                                                                {playerCount === 16 && (
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '10px'
                                                                    }}>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '160px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[4],
                                                                                        'round16',
                                                                                        2,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[4], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[4],
                                                                                    'round16',
                                                                                    2,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[5],
                                                                                        'round16',
                                                                                        2,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[5], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[5],
                                                                                    'round16',
                                                                                    2,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '265px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[6],
                                                                                        'round16',
                                                                                        3,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[6], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[6],
                                                                                    'round16',
                                                                                    3,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[7],
                                                                                        'round16',
                                                                                        3,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[7], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[7],
                                                                                    'round16',
                                                                                    3,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                {/* 8강 */}
                                                                {(playerCount === 16 || playerCount === 8) && (
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '10px'
                                                                    }}>
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '64px',
                                                                            top: '210px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={tournamentBracket.round8[2]?.name || ''}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round8[2],
                                                                                    'round8',
                                                                                    2,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={tournamentBracket.round8[3]?.name || ''}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round8[3],
                                                                                    'round8',
                                                                                    2,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>

                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {/* 4강 */}
                                                        {(playerCount === 16 || playerCount === 8 || playerCount === 4) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '128px',
                                                                top: '132.5px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '5px'
                                                            }}>
                                                                <TournamentSlot
                                                                    text={(() => {
                                                                        const slot = tournamentBracket.round4[0];
                                                                        const status = getSlotStatus(
                                                                            slot,
                                                                            'round4',
                                                                            0,
                                                                            0,
                                                                            window.currentPlayerCount || 16,
                                                                            window.currentGameMatches || []
                                                                        );
                                                                        console.log('렌더링 - round4[0]:', slot, 'status:', status);
                                                                        return slot?.name || '';
                                                                    })()}
                                                                    prop={getSlotStatus(
                                                                        tournamentBracket.round4[0],
                                                                        'round4',
                                                                        0,
                                                                        0,
                                                                        window.currentPlayerCount || 16,
                                                                        window.currentGameMatches || []
                                                                    )}
                                                                />
                                                                <TournamentSlot
                                                                    text={(() => {
                                                                        const slot = tournamentBracket.round4[1];
                                                                        const status = getSlotStatus(
                                                                            slot,
                                                                            'round4',
                                                                            0,
                                                                            1,
                                                                            window.currentPlayerCount || 16,
                                                                            window.currentGameMatches || []
                                                                        );
                                                                        console.log('렌더링 - round4[1]:', slot, 'status:', status);
                                                                        return slot?.name || '';
                                                                    })()}
                                                                    prop={getSlotStatus(
                                                                        tournamentBracket.round4[1],
                                                                        'round4',
                                                                        0,
                                                                        1,
                                                                        window.currentPlayerCount || 16,
                                                                        window.currentGameMatches || []
                                                                    )}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {/* 두 번째 16강~4강 그룹 */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: '0px',
                                                        top: '370px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '20px'
                                                    }}>
                                                        {/* 16강~8강 그룹 */}
                                                        <Box>
                                                            {/* 첫 번째 16강~8강 그룹 */}
                                                            <Box>
                                                                {/* 16강 그룹 */}
                                                                {playerCount === 16 && (
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '10px'
                                                                    }}>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '0px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[8],
                                                                                        'round16',
                                                                                        4,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[8], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[8],
                                                                                    'round16',
                                                                                    4,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[9],
                                                                                        'round16',
                                                                                        4,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[9], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[9],
                                                                                    'round16',
                                                                                    4,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '105px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[10],
                                                                                        'round16',
                                                                                        5,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[10], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[10],
                                                                                    'round16',
                                                                                    5,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[11],
                                                                                        'round16',
                                                                                        5,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[11], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[11],
                                                                                    'round16',
                                                                                    5,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                {/* 8강 */}
                                                                {(playerCount === 16 || playerCount === 8) && (
                                                                    <Box sx={{
                                                                        position: 'absolute',
                                                                        left: '64px',
                                                                        top: '52.5px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '5px'
                                                                    }}>
                                                                        <TournamentSlot
                                                                            text={(() => {
                                                                                const slotStatus = getSlotStatus(
                                                                                    tournamentBracket.round8[4],
                                                                                    'round8',
                                                                                    4,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                );
                                                                                return getSlotText(tournamentBracket.round8[4], slotStatus);
                                                                            })()}
                                                                            prop={getSlotStatus(
                                                                                tournamentBracket.round8[4],
                                                                                'round8',
                                                                                4,
                                                                                0,
                                                                                window.currentPlayerCount || 16,
                                                                                window.currentGameMatches || []
                                                                            )}
                                                                        />
                                                                        <TournamentSlot
                                                                            text={(() => {
                                                                                const slotStatus = getSlotStatus(
                                                                                    tournamentBracket.round8[5],
                                                                                    'round8',
                                                                                    4,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                );
                                                                                return getSlotText(tournamentBracket.round8[5], slotStatus);
                                                                            })()}
                                                                            prop={getSlotStatus(
                                                                                tournamentBracket.round8[5],
                                                                                'round8',
                                                                                4,
                                                                                1,
                                                                                window.currentPlayerCount || 16,
                                                                                window.currentGameMatches || []
                                                                            )}
                                                                        />
                                                                    </Box>
                                                                )}

                                                            </Box>
                                                            {/* 두 번째 16강~8강 그룹 */}
                                                            <Box>
                                                                {/* 16강 그룹 */}
                                                                {playerCount === 16 && (
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '10px'
                                                                    }}>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '160px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[12],
                                                                                        'round16',
                                                                                        6,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[12], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[12],
                                                                                    'round16',
                                                                                    6,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[13],
                                                                                        'round16',
                                                                                        6,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[13], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[13],
                                                                                    'round16',
                                                                                    6,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                        {/* 16강 */}
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '0',
                                                                            top: '265px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[14],
                                                                                        'round16',
                                                                                        7,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[14], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[14],
                                                                                    'round16',
                                                                                    7,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round16[15],
                                                                                        'round16',
                                                                                        7,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round16[15], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round16[15],
                                                                                    'round16',
                                                                                    7,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                {/* 8강 */}
                                                                {(playerCount === 16 || playerCount === 8) && (
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '10px'
                                                                    }}>
                                                                        <Box sx={{
                                                                            position: 'absolute',
                                                                            left: '64px',
                                                                            top: '210px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: '5px'
                                                                        }}>
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round8[6],
                                                                                        'round8',
                                                                                        6,
                                                                                        0,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round8[6], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round8[6],
                                                                                    'round8',
                                                                                    6,
                                                                                    0,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                            <TournamentSlot
                                                                                text={(() => {
                                                                                    const slotStatus = getSlotStatus(
                                                                                        tournamentBracket.round8[7],
                                                                                        'round8',
                                                                                        6,
                                                                                        1,
                                                                                        window.currentPlayerCount || 16,
                                                                                        window.currentGameMatches || []
                                                                                    );
                                                                                    return getSlotText(tournamentBracket.round8[7], slotStatus);
                                                                                })()}
                                                                                prop={getSlotStatus(
                                                                                    tournamentBracket.round8[7],
                                                                                    'round8',
                                                                                    6,
                                                                                    1,
                                                                                    window.currentPlayerCount || 16,
                                                                                    window.currentGameMatches || []
                                                                                )}
                                                                            />
                                                                        </Box>

                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {/* 4강 */}
                                                        {(playerCount === 16 || playerCount === 8 || playerCount === 4) && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: '128px',
                                                                top: '132.5px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '5px'
                                                            }}>
                                                                <TournamentSlot
                                                                    text={tournamentBracket.round4[2]?.name || ''}
                                                                    prop={getSlotStatus(
                                                                        tournamentBracket.round4[2],
                                                                        'round4',
                                                                        2,
                                                                        0,
                                                                        window.currentPlayerCount || 16,
                                                                        window.currentGameMatches || []
                                                                    )}
                                                                />
                                                                <TournamentSlot
                                                                    text={tournamentBracket.round4[3]?.name || ''}
                                                                    prop={getSlotStatus(
                                                                        tournamentBracket.round4[3],
                                                                        'round4',
                                                                        2,
                                                                        1,
                                                                        window.currentPlayerCount || 16,
                                                                        window.currentGameMatches || []
                                                                    )}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>

                                                {/* 최종 결승 라인 */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: '148px',
                                                    top: '343.5px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '5px',
                                                    alignItems: 'center'
                                                }}>
                                                    <TournamentSlot
                                                        text={tournamentBracket.final[0]?.name || ''}
                                                        prop={getSlotStatus(
                                                            tournamentBracket.final[0],
                                                            'final',
                                                            0,
                                                            0,
                                                            window.currentPlayerCount || 16,
                                                            window.currentGameMatches || []
                                                        )}
                                                    />
                                                    <TournamentSlot
                                                        text={tournamentBracket.final[1]?.name || ''}
                                                        prop={getSlotStatus(
                                                            tournamentBracket.final[1],
                                                            'final',
                                                            0,
                                                            1,
                                                            window.currentPlayerCount || 16,
                                                            window.currentGameMatches || []
                                                        )}
                                                    />
                                                </Box>
                                            </>
                                        );
                                    })()}
                                </Box>
                            </Box>
                        );
                    })()
            }
        </>
    );
};

export default ChampionshipDetailContent;